import React, { useMemo, useState } from "react";
import { MapContainer, Polyline, CircleMarker, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { MAP_CONFIG } from "../../config/api.config";
import { T, Btn, StatBox, SectionLabel, Field, Input, SearchableSelect } from "../shared/UI";
import { useHistory } from "../../hooks/useHistory";
import { usersApi } from "../../services/api.service";
import { fmt, STATUS } from "../../utils/helpers";
import { useApp } from "../../store/AppContext";
import LeafletMapCloseSearchableSelect from "../map/LeafletMapCloseSearchableSelect";
import BasemapLayer from "../map/BasemapLayer";
import { onlyOnAgents } from "../../utils/normalizers";

// Distinct colour per tracked agent (slot 0 = primary, slot 1 = compare)
const AGENT_COLORS = [T.acc, T.acc4];

// ── Map helpers ───────────────────────────────────────────────
function MapSync({ point }) {
  const map = useMap();
  React.useEffect(() => {
    if (point) map.panTo([point.lat, point.lng], { animate: true, duration: 0.3 });
  }, [point, map]);
  return null;
}

function makePlayheadIcon(color = T.acc) {
  return L.divIcon({
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    html: `<div style="width:32px;height:32px;border-radius:50%;background:${color}40;border:2.5px solid ${color};display:flex;align-items:center;justify-content:center;box-shadow:0 0 18px ${color}b3;font-size:14px;">📍</div>`,
  });
}

function makeStopIcon() {
  return L.divIcon({
    className: "",
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    html: `<div style="width:14px;height:14px;border-radius:50%;background:#f76a8a;border:2px solid #080c14;box-shadow:0 0 6px rgba(247,106,138,0.6);"></div>`,
  });
}

// Resolve a raw agentsMeta selection into a full agent object (falling back to a stub)
function resolveAgent(sel, agents) {
  if (!sel) return null;
  const found = agents.find(a => String(a.id) === String(sel.id));
  return found || {
    id: sel.id,
    fullName: sel.fullName,
    name: sel.fullName,
    mobile: sel.mobile,
    initials: (sel.fullName || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2),
    status: "offline",
    productId: sel.product?.id,
    productName: sel.product?.name,
  };
}

// ── Agent selector ────────────────────────────────────────────
function AgentSelector({ label, color, agents, selected, onSelect, allowClear }) {
  const options = useMemo(
    () => [
      { value: "", label: allowClear ? "— None —" : "— Choose an agent —" },
      ...agents.map(a => ({
        value: a.id,
        label: `${a.name || a.fullName || "Unnamed"}${a.product?.name ? ` · ${a.product.name}` : a.productName ? ` · ${a.productName}` : ""}${a.status ? ` (${a.status})` : ""}`,
      })),
    ],
    [agents, allowClear]
  );

  return (
    <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: 3, background: color, flexShrink: 0 }} />
        <div style={{ fontFamily: T.fontMono, fontSize: 9, letterSpacing: "1.5px", color: T.muted }}>{label}</div>
      </div>
      <SearchableSelect
        options={options}
        value={selected?.id || ""}
        onChange={(val) => {
          const agent = agents.find(a => String(a.id) === String(val));
          onSelect(agent || null);
        }}
        placeholder={allowClear ? "— None —" : "— Choose an agent —"}
      />
    </div>
  );
}

// Compact per-agent header + trip stats block (used for each tracked slot)
function AgentPanel({ slot, color }) {
  const agent = slot.agent;
  if (!agent) return null;
  const s = STATUS[agent?.status] || STATUS.offline;
  const cur = slot.currentPt;
  return (
    <div style={{ borderBottom: `1px solid ${T.border}` }}>
      <div style={{ ...styles.agentHeader, borderBottom: "none" }}>
        <div style={{ ...styles.avatar, background: s.bg, border: `2px solid ${color}`, color }}>
          {agent.initials}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: 3, background: color, flexShrink: 0 }} />
            {agent.name}
          </div>
          <div style={{ fontSize: 11, color: T.muted }}>{agent.mobile}</div>
          <div style={{ fontSize: 10, color: T.muted, marginTop: 2, fontFamily: T.fontMono }}>{agent.city || agent.address}</div>
        </div>
      </div>
      <div style={{ padding: "0 14px 12px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <StatBox label="Total Distance" value={`${slot.totalDist.toFixed(2)} km`} color={color} />
          <StatBox label="Avg Speed"      value={`${slot.avgSpeed} km/h`}          color={T.acc}  />
          <StatBox label="Max Speed"      value={`${slot.maxSpeed} km/h`}          color={T.acc4} />
          <StatBox label="GPS Points"     value={slot.path.length}                 color={T.acc2} />
        </div>
        {cur && (
          <div style={{ ...styles.coordBox, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <div style={styles.coordLabel}>LAT</div>
              <div style={{ ...styles.coordVal, color }}>{cur.lat.toFixed(5)}°</div>
            </div>
            <div style={styles.coordSep} />
            <div style={{ flex: 1 }}>
              <div style={styles.coordLabel}>LNG</div>
              <div style={{ ...styles.coordVal, color }}>{cur.lng.toFixed(5)}°</div>
            </div>
            <div style={styles.coordSep} />
            <div style={{ flex: 1 }}>
              <div style={styles.coordLabel}>SPD</div>
              <div style={{ ...styles.coordVal, color }}>{cur.speed} km/h</div>
            </div>
          </div>
        )}
        {cur && (
          <div style={{ fontSize: 11, color: T.muted, fontFamily: T.fontMono, marginTop: 6 }}>
            🕒 {fmt.time(cur.timestamp)} · pt {slot.currentIdx + 1}/{slot.path.length}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────
export default function HistoryView({ agents }) {
  const { historyAgent } = useApp();
  const [agentOptions, setAgentOptions] = useState([]);
  const [compare, setCompare]     = useState(false);
  const [localAgent, setLocalAgent]   = useState(historyAgent || agents[0] || null);
  const [localAgentB, setLocalAgentB] = useState(null);
  const [trailMode, setTrailMode] = useState("all");

  // Track one or two agents on a shared, time-synced timeline
  const trackedAgents = useMemo(
    () => (compare ? [localAgent, localAgentB] : [localAgent]),
    [compare, localAgent, localAgentB]
  );

  const {
    slots, loading, error,
    dateRange, setDateRange,
    playing, play, pause, reset,
    progress, seekProgress, nudge, toStart, toEnd,
    playSpeed, setPlaySpeed,
    gStart, gEnd, currentTime,
    refetch,
  } = useHistory(trackedAgents);

  const primary = slots[0] || {};

  const showPlayedTrail    = trailMode === "all" || trailMode === "played";
  const showRemainingTrail = trailMode === "all" || trailMode === "remaining";

  const mapCenter = (() => {
    const withPath = slots.find(sl => sl.path.length > 0);
    if (withPath) return [withPath.path[0].lat, withPath.path[0].lng];
    const a = localAgent;
    return a?.lat && a?.lng ? [a.lat, a.lng] : MAP_CONFIG.CENTER;
  })();

  // Speed chart — overlay each tracked agent (sample every 3rd point)
  const speedData = useMemo(() => {
    const rows = {};
    slots.forEach((sl, si) => {
      sl.path.filter((_, i) => i % 3 === 0).forEach((p, i) => {
        rows[i] = rows[i] || { i };
        rows[i][`speed${si}`] = p.speed;
      });
    });
    return Object.values(rows);
  }, [slots]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await usersApi.agentsMeta();
        const raw = Array.isArray(res.data?.data) ? res.data.data : [];
        if (mounted) setAgentOptions(onlyOnAgents(raw));
      } catch {
        if (mounted) setAgentOptions([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    if (localAgent) return;
    if (agentOptions.length === 0) return;
    setLocalAgent(resolveAgent(agentOptions[0], agents));
  }, [agentOptions, agents, localAgent]);

  return (
    <div style={styles.wrap}>
      {/* ── Left panel ── */}
      <div style={styles.leftPanel}>
        {/* Compare toggle */}
        <div style={{ ...styles.section, display: "flex", gap: 6 }}>
          {[
            { id: false, label: "Single agent" },
            { id: true,  label: "Compare 2 agents" },
          ].map(opt => (
            <button
              key={String(opt.id)}
              style={{ ...styles.toggleBtn, flex: 1, ...(compare === opt.id ? styles.toggleActive : {}) }}
              onClick={() => setCompare(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Agent A selector */}
        <AgentSelector
          label={compare ? "AGENT A" : "SELECT AGENT"}
          color={AGENT_COLORS[0]}
          agents={agentOptions}
          selected={localAgent}
          onSelect={(sel) => setLocalAgent(resolveAgent(sel, agents))}
        />

        {/* Agent B selector (compare mode only) */}
        {compare && (
          <AgentSelector
            label="AGENT B"
            color={AGENT_COLORS[1]}
            agents={agentOptions}
            selected={localAgentB}
            onSelect={(sel) => setLocalAgentB(resolveAgent(sel, agents))}
            allowClear
          />
        )}

        {/* Date range (shared) */}
        <div style={styles.section}>
          <SectionLabel>DATE RANGE</SectionLabel>
          <Field label="FROM">
            <Input type="datetime-local" value={dateRange.from} onChange={e => setDateRange(d => ({ ...d, from: e.target.value }))} />
          </Field>
          <Field label="TO">
            <Input type="datetime-local" value={dateRange.to}   onChange={e => setDateRange(d => ({ ...d, to:   e.target.value }))} />
          </Field>
          <Btn variant="primary" full onClick={refetch} disabled={loading || !localAgent}>
            {loading ? "Loading…" : "Load GPS History"}
          </Btn>
          {error && <div style={{ color: T.sos, fontSize: 11, marginTop: 8 }}>{error}</div>}
        </div>

        {/* Map trail mode */}
        <div style={styles.section}>
          <SectionLabel>MAP TRAIL</SectionLabel>
          <div style={styles.toggleRow}>
            {[
              { id: "all", label: "Full" },
              { id: "played", label: "Played" },
              { id: "remaining", label: "Remaining" },
              { id: "off", label: "Off" },
            ].map(opt => (
              <button
                key={opt.id}
                style={{ ...styles.toggleBtn, ...(trailMode === opt.id ? styles.toggleActive : {}) }}
                onClick={() => setTrailMode(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Per-agent info + trip stats + playhead */}
        {slots.map((sl, i) => (
          <AgentPanel key={i} slot={sl} color={AGENT_COLORS[i]} />
        ))}

        {/* Idle stops — primary agent */}
        {primary.stops?.length > 0 && (
          <div style={styles.section}>
            <SectionLabel>IDLE STOPS ({primary.stops.length})</SectionLabel>
            <div style={{ maxHeight: 200, overflowY: "auto" }}>
              {primary.stops.slice(0, 20).map((stop, i) => (
                <div key={i} style={styles.stopItem}>
                  <div style={styles.stopDot} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: T.text }}>Stopped · {fmt.time(stop.timestamp)}</div>
                    {stop.location && <div style={{ fontSize: 10, color: T.muted, marginTop: 1, lineHeight: 1.3 }}>{stop.location.trim()}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Map area ── */}
      <div style={styles.mapWrap}>
        {/* Map */}
        <div style={{ flex: 1, position: "relative" }}>
          <MapContainer center={mapCenter} zoom={13} style={{ width: "100%", height: "100%" }} zoomControl={false} maxZoom={20}>
            <BasemapLayer />
            <LeafletMapCloseSearchableSelect />
            {primary.currentPt && <MapSync point={primary.currentPt} />}

            {slots.map((sl, si) => {
              if (sl.path.length === 0) return null;
              const color = AGENT_COLORS[si];
              return (
                <React.Fragment key={si}>
                  {/* Ghost remaining path */}
                  {showRemainingTrail && sl.remainPath.length > 1 && (
                    <Polyline positions={sl.remainPath.map(p => [p.lat, p.lng])}
                      pathOptions={{ color: T.border2, weight: 2, dashArray: "4 6" }} />
                  )}

                  {/* Travelled path */}
                  {showPlayedTrail && sl.playedPath.length > 1 && (
                    <Polyline positions={sl.playedPath.map(p => [p.lat, p.lng])}
                      pathOptions={{ color, weight: 3 }} />
                  )}

                  {/* Start marker */}
                  <CircleMarker center={[sl.path[0].lat, sl.path[0].lng]} radius={8}
                    pathOptions={{ color, fillColor: color, fillOpacity: 0.9, weight: 2 }}>
                    <Popup><div style={styles.popup}>🟢 {sl.agent?.name} · Start<br />{fmt.time(sl.path[0].timestamp)}<br />{sl.path[0].location?.trim()}</div></Popup>
                  </CircleMarker>

                  {/* End marker */}
                  {sl.path.length > 1 && (
                    <CircleMarker center={[sl.path[sl.path.length - 1].lat, sl.path[sl.path.length - 1].lng]} radius={8}
                      pathOptions={{ color, fillColor: T.bg, fillOpacity: 1, weight: 3 }}>
                      <Popup><div style={styles.popup}>🏁 {sl.agent?.name} · End<br />{fmt.time(sl.path[sl.path.length - 1].timestamp)}<br />{sl.path[sl.path.length - 1].location?.trim()}</div></Popup>
                    </CircleMarker>
                  )}

                  {/* Stop markers */}
                  {sl.stops.map((stop, i) => (
                    <Marker key={i} position={[stop.lat, stop.lng]} icon={makeStopIcon()} zIndexOffset={500}>
                      <Popup><div style={styles.popup}>🔴 Stop<br />{fmt.time(stop.timestamp)}<br />{stop.location?.trim()}</div></Popup>
                    </Marker>
                  ))}

                  {/* Playhead */}
                  {sl.currentPt && (
                    <Marker position={[sl.currentPt.lat, sl.currentPt.lng]} icon={makePlayheadIcon(color)} zIndexOffset={1000 + si}>
                      <Popup><div style={styles.popup}>📍 {sl.agent?.name}<br />{fmt.time(sl.currentPt.timestamp)}<br />{sl.currentPt.speed} km/h<br />{sl.currentPt.location?.trim()}</div></Popup>
                    </Marker>
                  )}
                </React.Fragment>
              );
            })}
          </MapContainer>

          {/* Loading overlay */}
          {loading && (
            <div style={styles.loadingOverlay}>
              <div style={styles.loadingCard}>
                <div style={styles.spinner} />
                Loading GPS history…
              </div>
            </div>
          )}

          {/* Map legend */}
          <div style={styles.legend}>
            {slots.filter(sl => sl.agent && sl.path.length > 0).map((sl, i) => {
              const si = slots.indexOf(sl);
              return (
                <div key={si} style={styles.legendItem}>
                  <div style={{ ...styles.legendLine, background: AGENT_COLORS[si] }} />
                  {sl.agent?.name}
                </div>
              );
            })}
            <div style={styles.legendItem}><div style={{ ...styles.legendDot, background: "#f76a8a" }} />Stop</div>
          </div>
        </div>

        {/* Playback controls */}
        <div style={styles.controls}>
          {/* Progress bar */}
          <div style={styles.progressWrap}>
            <span style={styles.timeLabel}>{gEnd > gStart ? fmt.time(new Date(gStart).toISOString()) : "--:--"}</span>
            <div style={styles.progressTrack}
              onClick={e => {
                const rect = e.currentTarget.getBoundingClientRect();
                const pct = (e.clientX - rect.left) / rect.width;
                seekProgress(pct);
              }}>
              <div style={{ ...styles.progressFill, width: `${progress * 100}%` }} />
              <div style={{ ...styles.progressThumb, left: `${progress * 100}%` }} />
            </div>
            <span style={styles.timeLabel}>{gEnd > gStart ? fmt.time(new Date(gEnd).toISOString()) : "--:--"}</span>
          </div>

          <div style={styles.btnRow}>
            {/* Transport buttons */}
            <div style={styles.transportBtns}>
              <button style={styles.ctrlBtn} onClick={reset}                title="Reset">⏮</button>
              <button style={styles.ctrlBtn} onClick={() => nudge(-0.05)}    title="Back">⏪</button>
              <button style={{ ...styles.ctrlBtn, ...styles.playBtn }} onClick={playing ? pause : play}>
                {playing ? "⏸" : "▶"}
              </button>
              <button style={styles.ctrlBtn} onClick={() => nudge(0.05)}     title="Forward">⏩</button>
              <button style={styles.ctrlBtn} onClick={toEnd}                 title="End">⏭</button>
            </div>

            {/* Speed */}
            <div style={styles.speedRow}>
              <span style={{ fontSize: 10, color: T.muted, fontFamily: T.fontMono }}>SPEED</span>
              {[1, 3, 5, 10, 20].map(sp => (
                <button key={sp}
                  style={{ ...styles.speedBtn, ...(playSpeed === sp ? styles.speedActive : {}) }}
                  onClick={() => setPlaySpeed(sp)}>{sp}×</button>
              ))}
            </div>

            {/* Playhead clock */}
            <div style={{ fontFamily: T.fontMono, fontSize: 11, color: T.muted, marginLeft: "auto" }}>
              {currentTime != null ? fmt.time(new Date(currentTime).toISOString()) : `${Math.round(progress * 100)}%`}
            </div>
          </div>
        </div>

        {/* Speed chart */}
        {speedData.length > 3 && (
          <div style={styles.chart}>
            <div style={{ fontSize: 9, color: T.muted, fontFamily: T.fontMono, marginBottom: 4, letterSpacing: "1px" }}>
              SPEED PROFILE (km/h)
            </div>
            <ResponsiveContainer width="100%" height={65}>
              <LineChart data={speedData}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                <XAxis dataKey="i" hide />
                <YAxis hide domain={[0, "auto"]} />
                <Tooltip
                  contentStyle={{ background: T.s1, border: `1px solid ${T.border2}`, fontSize: 11, fontFamily: T.fontMono, color: T.text }}
                  formatter={(v, n) => [`${v} km/h`, slots[Number(String(n).replace("speed", ""))]?.agent?.name || "Speed"]}
                />
                {slots.map((sl, si) => sl.path.length > 0 && (
                  <Line key={si} type="monotone" dataKey={`speed${si}`} stroke={AGENT_COLORS[si]} dot={false} strokeWidth={1.5} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: { flex: 1, display: "flex", overflow: "hidden" },
  leftPanel: { width: 280, minWidth: 280, background: T.s1, borderRight: `1px solid ${T.border}`, overflowY: "auto", display: "flex", flexDirection: "column", position: "relative", zIndex: 5000 },
  agentHeader: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderBottom: `1px solid ${T.border}` },
  avatar: { width: 42, height: 42, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, fontFamily: T.fontMono, flexShrink: 0 },
  section: { padding: "12px 14px", borderBottom: `1px solid ${T.border}` },
  coordBox: { display: "flex", alignItems: "center", gap: 6, background: T.s2, borderRadius: 8, padding: "8px 10px", border: `1px solid ${T.border}` },
  coordLabel: { fontSize: 9, color: T.muted, fontFamily: T.fontMono },
  coordVal: { fontSize: 11, color: T.acc3, fontFamily: T.fontMono, fontWeight: 500 },
  coordSep: { width: 1, height: 26, background: T.border, flexShrink: 0 },
  stopItem: { display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0", borderBottom: `1px solid ${T.border}` },
  stopDot: { width: 8, height: 8, borderRadius: "50%", background: "#f76a8a", flexShrink: 0, marginTop: 3 },
  toggleRow: { display: "flex", flexWrap: "wrap", gap: 6 },
  toggleBtn: { padding: "5px 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: "none", color: T.muted, fontFamily: T.fontMono, fontSize: 10, cursor: "pointer" },
  toggleActive: { borderColor: T.acc, color: T.acc, background: "rgba(124,106,247,0.1)" },
  mapWrap: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative", zIndex: 1 },
  popup: { background: T.s1, color: T.text, fontFamily: T.fontSans, fontSize: 12, lineHeight: 1.5, minWidth: 160 },
  loadingOverlay: { position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  loadingCard: { background: T.s1, border: `1px solid ${T.border2}`, borderRadius: 12, padding: "16px 24px", fontFamily: T.fontMono, fontSize: 12, color: T.acc3, display: "flex", alignItems: "center", gap: 10 },
  spinner: { width: 16, height: 16, borderRadius: "50%", border: `2px solid rgba(106,247,196,0.3)`, borderTopColor: T.acc3, animation: "spin 0.8s linear infinite" },
  legend: { position: "absolute", bottom: 14, left: 14, background: T.s1, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "8px 12px", display: "flex", gap: 12, backdropFilter: "blur(8px)", zIndex: 500, maxWidth: "70%", flexWrap: "wrap" },
  legendItem: { display: "flex", alignItems: "center", gap: 5, fontSize: 10, fontFamily: T.fontMono, color: T.muted },
  legendDot: { width: 8, height: 8, borderRadius: "50%" },
  legendLine: { width: 16, height: 3, borderRadius: 2 },
  controls: { background: T.s1, borderTop: `1px solid ${T.border}`, padding: "10px 20px", flexShrink: 0 },
  progressWrap: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 },
  timeLabel: { fontFamily: T.fontMono, fontSize: 10, color: T.muted, flexShrink: 0 },
  progressTrack: { flex: 1, height: 4, background: T.border2, borderRadius: 2, cursor: "pointer", position: "relative" },
  progressFill: { position: "absolute", left: 0, top: 0, bottom: 0, background: T.acc, borderRadius: 2, transition: "width 0.1s" },
  progressThumb: { position: "absolute", top: "50%", transform: "translate(-50%,-50%)", width: 12, height: 12, borderRadius: "50%", background: T.acc, boxShadow: `0 0 8px ${T.acc}` },
  btnRow: { display: "flex", alignItems: "center", gap: 14 },
  transportBtns: { display: "flex", gap: 4 },
  ctrlBtn: { width: 32, height: 32, borderRadius: 8, background: T.s2, border: `1px solid ${T.border2}`, color: T.text, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" },
  playBtn: { background: T.acc, border: `1px solid ${T.acc}`, color: "#fff", width: 38, height: 38, borderRadius: 10, fontSize: 16 },
  speedRow: { display: "flex", alignItems: "center", gap: 4 },
  speedBtn: { padding: "3px 8px", borderRadius: 5, border: `1px solid ${T.border}`, background: "none", color: T.muted, fontFamily: T.fontMono, fontSize: 10, cursor: "pointer" },
  speedActive: { borderColor: T.acc, color: T.acc, background: "rgba(124,106,247,0.1)" },
  chart: { background: T.s1, borderTop: `1px solid ${T.border}`, padding: "8px 20px 6px", flexShrink: 0 },
};
